import React, { Context, ReactElement, memo, useContext, useEffect, useState } from 'react';
import { t } from 'i18next';
import { Status, Wrapper } from '@googlemaps/react-wrapper';
import { Market } from '@oneaudi/avp-models';
import styled from 'styled-components';
import { extractKvpsId } from '../../util/dealer-data';
import { GiveawayContext } from '../../services/GiveawayContextService';
import { Spinner } from '../spinner/Spinner';
import { CupraApiClient } from '../../client/CupraApiClient';
import { ApiClientContext } from '../../services/ApiClientService';
import { DealerData } from '../../constants/Interfaces';
import { MyDealerMap } from './myDealerMap';
import email from '../../assets/email.svg';
import phone from '../../assets/phone.svg';
import { HorizontalSpacing, Spacing, VerticalSpacing } from '../../common/spacings';
import { screen } from '../../common/breakpoints';

const StyledMyDealerContainer = styled.div`
  padding: ${VerticalSpacing.S};
  height: auto;

  @media screen and ${screen.m} {
    padding: ${HorizontalSpacing.M};
  }

  @media screen and ${screen.l} {
    padding: ${HorizontalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding: ${HorizontalSpacing.XL};
  }

  @media screen and ${screen.xxl} {
    padding: ${HorizontalSpacing.XL};
  }

  h2 {
    padding-bottom: ${VerticalSpacing.S};

    @media screen and ${screen.m} {
      padding-bottom: ${VerticalSpacing.M};
    }

    @media screen and ${screen.l} {
      padding-bottom: ${VerticalSpacing.L};
    }

    @media screen and ${screen.xl} {
      padding-bottom: ${VerticalSpacing.XL};
    }

    @media screen and ${screen.xxl} {
      padding-bottom: ${VerticalSpacing.XL};
    }
  }
`;

const StyledMapContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: ${VerticalSpacing.S};

  @media screen and ${screen.l} {
    flex-direction: row;
    padding-bottom: 0;
  }
`;

const StyledMap = styled.div`
  flex-grow: 1;
  max-width: 100%;
  height: 300px;

  @media screen and ${screen.m} {
    height: 450px;
  }

  @media screen and ${screen.l} {
    flex-basis: calc(66% - 10px);
    height: 600px;
  }
`;

const StyledInformationContainer = styled.div`
  flex-grow: 1;
  max-width: 100%;
  height: 200px;

  @media screen and ${screen.l} {
    flex-basis: calc(33% - 10px);
  }
`;

const StyledAddress = styled.div`
  padding-bottom: ${VerticalSpacing.S};
  padding-top: ${VerticalSpacing.S};

  @media screen and ${screen.m} {
    padding-bottom: ${VerticalSpacing.S};
    padding-top: ${VerticalSpacing.S};
  }

  @media screen and ${screen.l} {
    padding-bottom: ${VerticalSpacing.S};
    padding-top: ${VerticalSpacing.M};
  }

  @media screen and ${screen.xl} {
    padding-bottom: ${VerticalSpacing.M};
    padding-top: ${VerticalSpacing.L};
  }

  @media screen and ${screen.xxl} {
    padding-bottom: ${VerticalSpacing.M};
    padding-top: ${VerticalSpacing.L};
  }
`;

const StyledContact = styled.div`
  padding-bottom: ${VerticalSpacing.S};

  @media screen and ${screen.m} {
    padding-bottom: ${VerticalSpacing.M};
  }

  @media screen and ${screen.l} {
    padding-bottom: ${VerticalSpacing.L};
  }

  @media screen and ${screen.xl} {
    padding-bottom: ${VerticalSpacing.XL};
  }

  @media screen and ${screen.xxl} {
    padding-bottom: ${VerticalSpacing.XL};
  }
`;

const StyledThumbnail = styled.div`
  a {
    text-decoration: none;
    color: black;
  }
  img {
    padding-right: ${Spacing.S};
  }
`;

const hasCompleteDealerData = (data: DealerData): boolean => {
  const requiredProps: (keyof DealerData)[] = [
    'name',
    'street',
    'city',
    'zipCode',
    'phone',
    'email',
    'website',
  ];
  return requiredProps.every((prop) => {
    return data[prop];
  });
};

// eslint-disable-next-line react/display-name
export const MyDealer = memo((): ReactElement => {
  const { dealerId, market, locale } = useContext(GiveawayContext);
  const hckClient = useContext<CupraApiClient>(ApiClientContext as Context<CupraApiClient>);

  const kvpsId = extractKvpsId(dealerId);
  const [dealerData, setDealerData] = useState<DealerData>();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    const fetchData = async (market: Market, kvpsId: string) => {
      try {
        const response = await hckClient.getDealerInformation(market, kvpsId);
        setDealerData(response);
      } catch (error: any) {
        console.error('Could not retrieve Dealer Data');
      }
    };
    if (kvpsId) {
      fetchData(market, kvpsId);
    }
  }, [dealerId, hckClient, market]);

  if (!apiKey || !dealerData || !hasCompleteDealerData(dealerData)) {
    return <></>;
  }

  if (!dealerData.coordinates.latitude || !dealerData.coordinates.longitude) {
    return <></>;
  }

  const render = (status: Status): ReactElement => {
    if (status === Status.LOADING) return <></>;
    return <Spinner locale={locale} />;
  };
  /* This API Key is restricted to only be able to make calls from the dga cupra websites
    and localhost to the defined google maps apis in our gcp account
    It is okay and needed to expose this unfortunately, see: 
    https://developers.google.com/maps/documentation/javascript/overview?hl=de
    https://stackoverflow.com/questions/39625587/how-do-i-securely-use-google-api-keys, 
    https://stackoverflow.com/questions/1364858/what-steps-should-i-take-to-protect-my-google-maps-api-key 
    */
  return (
    <StyledMyDealerContainer role="myDealer-container">
      <Wrapper apiKey={apiKey} render={render} data-testid="myDealer-wrapper">
        <h2>{t('myDealer.title')}</h2>
        <StyledMapContainer>
          <StyledInformationContainer data-testid="myDealer-name">
            <h3>{dealerData.name}</h3>
            <StyledAddress data-testid="myDealer-address">
              <div>{dealerData.street}</div>
              <div>
                {dealerData.zipCode} {dealerData.city}
              </div>
            </StyledAddress>
            <StyledContact data-testid="myDealer-contact">
              <StyledThumbnail>
                <img src={phone} alt="myDealer-phone-thumbnail" />
                <a href={`tel:${dealerData.phone}`}>{dealerData.phone}</a>
              </StyledThumbnail>
              <StyledThumbnail>
                <img src={email} alt="myDealer-mail-thumbnail" />
                <a href={`mailto:${dealerData.email}`}>{dealerData.email}</a>
              </StyledThumbnail>
              <div>{dealerData.website}</div>
            </StyledContact>
          </StyledInformationContainer>
          <StyledMap data-testid="myDealer-map">
            <MyDealerMap
              center={{
                lat: dealerData.coordinates.latitude,
                lng: dealerData.coordinates.longitude,
              }}
              zoom={15}
            />
          </StyledMap>
        </StyledMapContainer>
      </Wrapper>
    </StyledMyDealerContainer>
  );
});
